import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Shield, ShieldCheck, ShieldOff, RefreshCw, KeyRound, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

export default function Profile() {
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Cambiar contraseña
  const [pwdCurrent, setPwdCurrent]   = useState('');
  const [pwdNew, setPwdNew]           = useState('');
  const [pwdConfirm, setPwdConfirm]   = useState('');
  const [pwdLoading, setPwdLoading]   = useState(false);
  const [pwdError, setPwdError]       = useState('');
  const [pwdSuccess, setPwdSuccess]   = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwdError('');
    if (pwdNew !== pwdConfirm) { setPwdError('Las contraseñas nuevas no coinciden.'); return; }
    if (pwdNew.length < 8) { setPwdError('La nueva contraseña debe tener al menos 8 caracteres.'); return; }
    setPwdLoading(true);
    try {
      const r = await fetch(`${API_URL}/auth/change-password`, {
        method: 'POST', headers,
        body: JSON.stringify({ current_password: pwdCurrent, new_password: pwdNew }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setPwdSuccess(true);
      setPwdCurrent(''); setPwdNew(''); setPwdConfirm('');
      setTimeout(() => setPwdSuccess(false), 4000);
    } catch (err) {
      setPwdError(err.message);
    } finally {
      setPwdLoading(false);
    }
  };

  const [twoFaEnabled, setTwoFaEnabled] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(true);

  // Setup flow
  const [setupStep, setSetupStep] = useState('idle'); // idle | qr | confirm | done
  const [qrData, setQrData]       = useState('');
  const [secret, setSecret]       = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupError, setSetupError]     = useState('');

  // Disable flow
  const [disableStep, setDisableStep] = useState('idle'); // idle | confirm
  const [disablePwd, setDisablePwd]   = useState('');
  const [disableLoading, setDisableLoading] = useState(false);
  const [disableError, setDisableError]     = useState('');

  const load2faStatus = async () => {
    setLoadingStatus(true);
    try {
      const r = await fetch(`${API_URL}/auth/2fa/status`, { headers });
      const d = await r.json();
      setTwoFaEnabled(d.enabled || false);
    } finally {
      setLoadingStatus(false);
    }
  };

  useEffect(() => { load2faStatus(); }, []); // eslint-disable-line

  // ── Setup ────────────────────────────────────────────────────────────────

  const handleSetupStart = async () => {
    setSetupLoading(true);
    setSetupError('');
    try {
      const r = await fetch(`${API_URL}/auth/2fa/setup`, { method: 'POST', headers });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setQrData(d.qr);
      setSecret(d.secret);
      setSetupStep('qr');
    } catch (e) {
      setSetupError(e.message);
    } finally {
      setSetupLoading(false);
    }
  };

  const handleSetupConfirm = async (e) => {
    e.preventDefault();
    if (confirmCode.replace(/\s/g, '').length < 6) { setSetupError('Ingresá los 6 dígitos.'); return; }
    setSetupLoading(true);
    setSetupError('');
    try {
      const r = await fetch(`${API_URL}/auth/2fa/enable`, {
        method: 'POST', headers,
        body: JSON.stringify({ code: confirmCode }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setTwoFaEnabled(true);
      setSetupStep('done');
      setConfirmCode('');
    } catch (e) {
      setSetupError(e.message);
      setConfirmCode('');
    } finally {
      setSetupLoading(false);
    }
  };

  // ── Disable ──────────────────────────────────────────────────────────────

  const handleDisable = async (e) => {
    e.preventDefault();
    setDisableLoading(true);
    setDisableError('');
    try {
      const r = await fetch(`${API_URL}/auth/2fa/disable`, {
        method: 'POST', headers,
        body: JSON.stringify({ password: disablePwd }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setTwoFaEnabled(false);
      setDisableStep('idle');
      setDisablePwd('');
    } catch (e) {
      setDisableError(e.message);
    } finally {
      setDisableLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: '28px 24px' }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--ink)', margin: 0 }}>Mi perfil</h1>
        <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 4 }}>
          {user?.full_name} · {user?.email} · {user?.role}
        </div>
      </div>

      {/* ── Tarjeta Cambiar Contraseña ── */}
      <div className="card" style={{ padding: 24, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ width: 42, height: 42, borderRadius: 12, background: 'var(--bg-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <KeyRound size={20} style={{ color: 'var(--accent)' }} />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>Cambiar contraseña</div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>Actualizá tu contraseña de acceso</div>
          </div>
        </div>

        {pwdSuccess && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--green-dim)', border: '1px solid var(--green-dim)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--green)', marginBottom: 16 }}>
            <Check size={15} /> Contraseña actualizada correctamente
          </div>
        )}
        {pwdError && <ErrorBox msg={pwdError} />}

        <form onSubmit={handleChangePassword} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Contraseña actual</label>
            <input
              type="password" value={pwdCurrent} onChange={e => setPwdCurrent(e.target.value)}
              required placeholder="••••••••"
              style={{ width: '100%', padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Nueva contraseña</label>
            <input
              type="password" value={pwdNew} onChange={e => setPwdNew(e.target.value)}
              required placeholder="Mínimo 8 caracteres"
              style={{ width: '100%', padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Confirmar nueva contraseña</label>
            <input
              type="password" value={pwdConfirm} onChange={e => setPwdConfirm(e.target.value)}
              required placeholder="Repetí la nueva contraseña"
              style={{ width: '100%', padding: '10px 14px', border: `1.5px solid ${pwdConfirm && pwdNew !== pwdConfirm ? 'var(--red-dim)' : 'var(--border)'}`, borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
            />
            {pwdConfirm && pwdNew !== pwdConfirm && (
              <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 4 }}>Las contraseñas no coinciden</div>
            )}
          </div>
          <button type="submit" disabled={pwdLoading || (pwdConfirm && pwdNew !== pwdConfirm)} style={btnPrimary(pwdLoading)}>
            {pwdLoading ? <><RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> Actualizando...</> : <><KeyRound size={14} /> Actualizar contraseña</>}
          </button>
        </form>
      </div>

      {/* ── Tarjeta 2FA ── */}
      <div className="card" style={{ padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{
            width: 42, height: 42, borderRadius: 12,
            background: twoFaEnabled ? 'var(--green-dim)' : 'var(--bg-subtle)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {loadingStatus
              ? <RefreshCw size={18} style={{ color: 'var(--ink-3)', animation: 'spin 1s linear infinite' }} />
              : twoFaEnabled
                ? <ShieldCheck size={20} style={{ color: 'var(--green)' }} />
                : <ShieldOff size={20} style={{ color: 'var(--ink-3)' }} />
            }
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>
              Autenticación en dos factores (2FA)
            </div>
            <div style={{ fontSize: 12, color: twoFaEnabled ? 'var(--green)' : 'var(--ink-3)', fontWeight: 500 }}>
              {loadingStatus ? 'Cargando...' : twoFaEnabled ? '✓ Activo' : 'Desactivado'}
            </div>
          </div>
        </div>

        {/* ─ Estado: desactivado ─ */}
        {!twoFaEnabled && setupStep === 'idle' && (
          <>
            <p style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 16, lineHeight: 1.6 }}>
              Protegé tu cuenta con una app autenticadora (Google Authenticator, Authy, etc.).
              Cada vez que inicies sesión, necesitarás ingresar un código de 6 dígitos que cambia cada 30 segundos.
            </p>
            {setupError && <ErrorBox msg={setupError} />}
            <button onClick={handleSetupStart} disabled={setupLoading} style={btnPrimary(setupLoading)}>
              <Shield size={15} />
              {setupLoading ? 'Generando...' : 'Activar 2FA'}
            </button>
          </>
        )}

        {/* ─ Paso 1 setup: mostrar QR ─ */}
        {setupStep === 'qr' && (
          <>
            <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 16, lineHeight: 1.6 }}>
              <strong>Paso 1:</strong> Escaneá este código QR con tu app autenticadora.
            </div>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <img src={qrData} alt="QR 2FA" style={{ width: 200, height: 200, borderRadius: 12, border: '1px solid var(--border)' }} />
            </div>
            <details style={{ marginBottom: 16 }}>
              <summary style={{ fontSize: 12, color: 'var(--ink-2)', cursor: 'pointer' }}>
                No podés escanear el QR? Ingresá el código manualmente
              </summary>
              <div style={{ marginTop: 8, padding: '10px 12px', background: 'var(--bg-subtle)', borderRadius: 8, fontFamily: 'monospace', fontSize: 13, letterSpacing: '0.1em', wordBreak: 'break-all', color: 'var(--ink-2)' }}>
                {secret}
              </div>
            </details>
            <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 12 }}>
              <strong>Paso 2:</strong> Ingresá el código de 6 dígitos que muestra la app para confirmar.
            </div>
            {setupError && <ErrorBox msg={setupError} />}
            <form onSubmit={handleSetupConfirm} style={{ display: 'flex', gap: 10 }}>
              <input
                type="text" inputMode="numeric" pattern="[0-9 ]*" maxLength={7}
                placeholder="000 000"
                value={confirmCode}
                onChange={e => setConfirmCode(e.target.value.replace(/[^0-9 ]/g, ''))}
                style={{ flex: 1, padding: '10px 14px', border: '1.5px solid #ddd', borderRadius: 8, fontSize: 20, fontWeight: 700, letterSpacing: '0.3em', textAlign: 'center' }}
                autoFocus
              />
              <button type="submit" disabled={setupLoading} style={{ ...btnPrimary(setupLoading), width: 'auto', padding: '10px 20px' }}>
                {setupLoading ? '...' : 'Confirmar'}
              </button>
            </form>
            <button onClick={() => { setSetupStep('idle'); setSetupError(''); }} style={btnCancel}>
              Cancelar
            </button>
          </>
        )}

        {/* ─ Setup completado ─ */}
        {setupStep === 'done' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>✅</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--green)', marginBottom: 6 }}>2FA activado correctamente</div>
            <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>
              A partir de ahora, cada inicio de sesión requerirá el código de tu app autenticadora.
            </div>
            <button onClick={() => setSetupStep('idle')} style={{ ...btnPrimary(false), marginTop: 16, width: 'auto', padding: '8px 20px' }}>
              Listo
            </button>
          </div>
        )}

        {/* ─ Estado: activo — desactivar ─ */}
        {twoFaEnabled && setupStep === 'idle' && (
          <>
            <div style={{ padding: '12px 16px', background: 'var(--green-dim)', border: '1px solid var(--green-dim)', borderRadius: 10, marginBottom: 16, fontSize: 13, color: 'var(--green)' }}>
              Tu cuenta está protegida con autenticación en dos factores.
            </div>
            {disableStep === 'idle' && (
              <button onClick={() => setDisableStep('confirm')} style={btnDanger}>
                <ShieldOff size={14} /> Desactivar 2FA
              </button>
            )}
            {disableStep === 'confirm' && (
              <>
                <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 12 }}>
                  Para desactivar 2FA, confirmá tu contraseña actual.
                </div>
                {disableError && <ErrorBox msg={disableError} />}
                <form onSubmit={handleDisable} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <input
                    type="password" placeholder="Contraseña actual"
                    value={disablePwd}
                    onChange={e => setDisablePwd(e.target.value)}
                    required autoFocus
                    style={{ padding: '10px 14px', border: '1.5px solid var(--red-dim)', borderRadius: 8, fontSize: 14 }}
                  />
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button type="submit" disabled={disableLoading} style={{ ...btnDanger, flex: 1 }}>
                      {disableLoading ? 'Desactivando...' : 'Confirmar desactivación'}
                    </button>
                    <button type="button" onClick={() => { setDisableStep('idle'); setDisableError(''); setDisablePwd(''); }} style={btnCancel}>
                      Cancelar
                    </button>
                  </div>
                </form>
              </>
            )}
          </>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function ErrorBox({ msg }) {
  return (
    <div style={{ background: 'var(--red-dim)', border: '1px solid var(--red-dim)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--red)', marginBottom: 12 }}>
      {msg}
    </div>
  );
}

const btnPrimary = (disabled) => ({
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  width: '100%', padding: '11px 16px', background: 'var(--ink)', color: 'var(--surface)',
  border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600,
  cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.7 : 1,
});

const btnDanger = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  width: '100%', padding: '10px 16px', background: 'var(--red-dim)', color: 'var(--red)',
  border: '1px solid var(--red-dim)', borderRadius: 8, fontSize: 13, fontWeight: 600,
  cursor: 'pointer',
};

const btnCancel = {
  marginTop: 8, background: 'none', border: 'none', color: 'var(--ink-3)',
  fontSize: 13, cursor: 'pointer', textDecoration: 'underline', width: '100%', textAlign: 'center',
};
